import en from './en';
import type { AdminUiCopy } from './types';

type ExtendedSections = Pick<
  AdminUiCopy,
  | 'ownerStaff'
  | 'ownerBlogManager'
  | 'ownerBlogEditor'
  | 'superAdminDashboard'
  | 'superAdminSecurity'
  | 'superAdminUsers'
  | 'superAdminDemoRequests'
  | 'superAdminUserDetail'
>;

const frExtendedSections: ExtendedSections = {
  ownerStaff: {
    ownerTools: 'Outils propriétaire',
    notAuthorized: 'Non autorisé',
    restrictedBody:
      'Cette zone est réservée aux propriétaires de la plateforme. Les outils propriétaire pilotent les accès staff et écrivent directement dans les permissions de production.',
    ownerAccessRequired: 'Accès propriétaire requis',
    notInOwnerEmails: 'Votre e-mail {email} n’est pas listé dans PLATFORM_OWNER_EMAILS.',
    addAndReauth:
      'Ajoutez-le puis reconnectez-vous via Google/GitHub SSO. En production, le statut propriétaire est évalué depuis la configuration d’environnement et la session SSO.',
    manageStaffBody:
      'Gérez les accès staff (super admin). Les changements s’appliquent immédiatement et sont journalisés pour audit. Utilisez les outils Super Admin pour suspendre ou réactiver les comptes clients.',
    grantRevokeTitle: 'Accès staff: octroyer/révoquer',
    email: 'E-mail',
    action: 'Action',
    reason: 'Raison',
    grantStaff: 'Octroyer staff',
    revokeStaff: 'Révoquer staff',
    working: 'Traitement...',
    enterEmail: 'Saisissez un e-mail pour continuer',
    provideReason: 'Veuillez indiquer une raison (pour les journaux d’audit)',
    currentStaff: 'Comptes staff actuels',
    noStaff: 'Aucun compte staff.',
    tableEmail: 'E-mail',
    tableId: 'ID',
    tableActions: 'Actions',
    revoke: 'Révoquer',
    prev: 'Préc.',
    next: 'Suiv.',
    pageOf: 'Page {page} sur {total}',
    recentActions: 'Actions récentes du propriétaire',
    noRecentActions: 'Aucune action récente.',
    grantDialogTitle: 'Octroyer l’accès staff ?',
    revokeDialogTitle: 'Révoquer l’accès staff ?',
    grantDialogDescription: 'Accorde un accès plateforme complet à {email}.',
    revokeDialogDescription: 'Retire l’accès plateforme à {email}.',
    confirmGrant: 'Octroyer',
    confirmRevoke: 'Révoquer',
    grantSuccess: 'Accès staff octroyé pour {email}',
    revokeSuccess: 'Accès staff révoqué pour {email}',
    failedUpdateStaff: 'Impossible de mettre à jour le staff',
    failedRevoke: 'Échec de la révocation',
    revokeReasonPlaceholder: 'Raison de la révocation',
    revokeReasonHint: 'Utilisé pour les journaux d’audit ; restez concis.',
    ownerManagedUpdateDefault: 'Mise à jour staff gérée par le propriétaire',
    accessNoLongerRequiredDefault: 'Accès non requis',
  },
  ownerBlogManager: {
    title: 'Gestionnaire de blog',
    subtitle: 'Créer et gérer les articles de blog',
    accessRestricted: 'Accès restreint',
    accessBody: 'La gestion du blog nécessite un accès propriétaire ou super admin.',
    newPost: 'Nouvel article',
    all: 'Tous',
    draft: 'Brouillon',
    published: 'Publié',
    loadingPosts: 'Chargement des articles...',
    noPosts: 'Aucun article pour le moment. Créez votre premier article.',
    createFirstPost: 'Créez votre premier article pour publier des mises à jour.',
    createPost: 'Créer un article',
    tableTitle: 'Titre',
    tableStatus: 'Statut',
    tableAuthor: 'Auteur',
    tableDate: 'Date',
    tableActions: 'Actions',
    edit: 'Modifier',
    view: 'Voir',
    delete: 'Supprimer',
    previous: 'Précédent',
    next: 'Suivant',
    pageOf: 'Page {page} sur {total}',
    deleteTitle: 'Supprimer l’article ?',
    deleteBody: 'Cette action est irréversible. L’article sera supprimé définitivement.',
    cancel: 'Annuler',
    confirmDelete: 'Supprimer',
    toastPostDeleted: 'Article supprimé',
    toastDeleteFailed: 'Échec de la suppression de l’article',
  },
  ownerBlogEditor: {
    title: 'Éditeur de blog',
    accessRestricted: 'Accès restreint',
    accessBody: 'La gestion du blog nécessite un accès propriétaire ou super admin.',
    editPost: 'Modifier l’article',
    newPost: 'Nouvel article',
    backToManager: 'Retour au gestionnaire de blog',
    saveDraft: 'Enregistrer le brouillon',
    publish: 'Publier',
    saving: 'Enregistrement...',
    publishing: 'Publication...',
    postTitlePlaceholder: 'Titre de l’article',
    status: 'Statut',
    draft: 'Brouillon',
    published: 'Publié',
    slug: 'Slug URL',
    slugPlaceholder: 'slug-article',
    excerpt: 'Extrait',
    excerptPlaceholder: 'Résumé court pour la liste du blog...',
    featuredImageUrl: 'URL de l’image à la une',
    tags: 'Tags',
    tagsPlaceholder: 'EUDI, Mise à jour produit, Engineering',
    commaSeparated: 'Séparés par des virgules',
    authorName: 'Nom de l’auteur',
    authorNamePlaceholder: 'Nom de l’auteur',
    authorAvatarUrl: 'URL de l’avatar auteur',
    remove: 'Retirer',
    importMarkdown: 'Importer du Markdown',
    markdown: 'Markdown',
    markdownPlaceholder: 'Collez votre markdown ici...',
    preview: 'Aperçu',
    previewPlaceholder: 'L’aperçu s’affichera ici...',
    cancel: 'Annuler',
    import: 'Importer',
    titleRequired: 'Le titre est requis',
    contentRequired: 'Le contenu est requis',
    failedLoadPost: 'Impossible de charger l’article',
    recoveredDraft: 'Brouillon non enregistré restauré',
    postPublished: 'Article publié',
    draftSaved: 'Brouillon enregistré',
    draftCreated: 'Brouillon créé',
    failedSavePost: 'Impossible d’enregistrer l’article',
    imageUploaded: 'Image importée',
    avatarUploaded: 'Avatar importé',
    uploadFailed: 'Échec de l’upload',
    markdownImported: 'Markdown importé',
    startWritingPlaceholder: 'Commencez à rédiger votre article de blog...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Chargement des métriques plateforme...',
    platformOverview: 'Vue d’ensemble plateforme',
    globalMetrics: 'Métriques globales et analyses de performance',
    noMetrics: 'Aucune métrique disponible',
    quickActions: 'Actions rapides',
    platformSecurity: 'Sécurité plateforme',
    viewAllUsers: 'Voir tous les utilisateurs',
    demoRequests: 'Demandes de démo',
    trialUsers: 'Utilisateurs essai',
    suspendedUsers: 'Utilisateurs suspendus',
    userMetrics: 'Métriques utilisateurs',
    totalUsers: 'Utilisateurs totaux',
    activeUsers: 'Utilisateurs actifs',
    activePaidUsers: 'Utilisateurs payants actifs',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Revenu mensuel récurrent',
    ofTotal: '{value}% du total',
    ofActiveUsers: '{value}% des utilisateurs actifs',
    planDistribution: 'Répartition des offres',
    trial: 'Essai',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Utilisateurs actifs{totalSuffix}',
    verificationSessions: 'Sessions de vérification',
    totalSessions: 'Sessions totales',
    thisMonth: 'Ce mois-ci',
    lastMonth: 'Mois dernier',
    growth: 'Croissance',
    liveVerifications: 'Vérifications live',
    allTime: 'Cumul',
    apiKeys: 'Clés API',
    totalKeys: 'Clés totales',
    activeKeys: 'Clés actives',
    liveKeys: 'Clés live',
    productionEnvironment: 'Environnement de production',
  },
  superAdminSecurity: {
    superAdmin: 'Super Admin',
    platformSecurity: 'Sécurité plateforme',
    subtitle: 'Vue des anomalies inter-marchands pour les comptes staff.',
    totalAlerts: 'Alertes totales',
    activeAlerts: 'Alertes actives',
    critical: 'Critique',
    topMerchantsLast7d: 'Top marchands (7 j)',
    filters: 'Filtres',
    severity: 'Sévérité',
    status: 'Statut',
    merchantIdOptional: 'ID marchand (optionnel)',
    show: 'Afficher',
    any: 'Tous',
    low: 'Faible',
    medium: 'Moyenne',
    high: 'Élevée',
    active: 'Active',
    investigating: 'En investigation',
    resolved: 'Résolue',
    falsePositive: 'Faux positif',
    refresh: 'Actualiser',
    securityAlerts: 'Alertes de sécurité',
    noAlerts: 'Aucune alerte pour les filtres actuels.',
    time: 'Heure',
    merchant: 'Marchand',
    type: 'Type',
    pageOf: 'Page {page} sur {total}',
    prev: 'Préc.',
    next: 'Suiv.',
  },
  superAdminUsers: {
    superAdmin: 'Super Admin',
    allUsers: 'Tous les utilisateurs',
    subtitle: 'Gestion des utilisateurs et analytics à l’échelle de la plateforme',
    totalUsers: 'Utilisateurs totaux',
    activeUsers: 'Utilisateurs actifs',
    trialUsers: 'Utilisateurs essai',
    paidUsers: 'Utilisateurs payants',
    filters: 'Filtres',
    exporting: 'Export en cours...',
    exportCsv: 'Exporter CSV',
    hideFilters: 'Masquer les filtres',
    showFilters: 'Afficher les filtres',
    search: 'Recherche',
    searchPlaceholder: 'E-mail, nom ou entreprise...',
    planTier: 'Niveau d’offre',
    allTiers: 'Tous les niveaux',
    trial: 'Essai',
    pro: 'Pro',
    business: 'Business',
    status: 'Statut',
    allStatuses: 'Tous les statuts',
    active: 'Actif',
    suspended: 'Suspendu',
    loadingUsers: 'Chargement des utilisateurs...',
    noUsersFound: 'Aucun utilisateur trouvé',
    tableUser: 'Utilisateur',
    tablePlan: 'Offre',
    tablePlanActive: 'Offre active',
    tableUsage: 'Utilisation',
    tableResources: 'Ressources',
    tableCreated: 'Créé',
    tableActions: 'Actions',
    inactive: 'Inactif',
    admin: 'Admin',
    liveThisMonth: 'live ce mois-ci',
    keys: 'clés',
    sessions: 'sessions',
    updated: 'Mis à jour',
    viewSelf: 'Voir mon compte',
    viewDetails: 'Voir détails',
    usage: 'Utilisation',
    resources: 'Ressources',
    created: 'Créé',
    showingRange: '{start} à {end} sur {total} utilisateurs',
    previous: 'Précédent',
    next: 'Suivant',
    exportStarted: 'Export CSV démarré.',
    unableExport: 'Impossible d’exporter les utilisateurs.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super Admin',
    demoRequests: 'Demandes de démo',
    subtitle: 'Suivez et gérez les demandes de démo des prospects',
    totalRequests: 'Demandes totales',
    pending: 'En attente',
    contacted: 'Contacté',
    completed: 'Terminé',
    allRequests: 'Toutes les demandes',
    pendingOnly: 'En attente uniquement',
    scheduled: 'Planifié',
    onboarded: 'Onboardé',
    followUp: 'Relance',
    churned: 'Perdu',
    showing: '{count} sur {total} demandes',
    loading: 'Chargement des demandes de démo...',
    noRequestsFound: 'Aucune demande de démo trouvée',
    noRequestsAll:
      'Aucune demande de démo soumise pour le moment. Elles apparaîtront ici dès qu’un client réservera une démo sur votre landing page.',
    noRequestsWithStatus: 'Aucune demande avec le statut "{status}". Essayez un autre filtre.',
    contact: 'Contact',
    quickInfo: 'Info rapide',
    status: 'Statut',
    submitted: 'Soumise',
    actions: 'Actions',
    hideDetails: 'Masquer les détails',
    showDetails: 'Afficher les détails',
    noInfo: 'Aucune information',
    email: 'E-mail',
    role: 'Rôle',
    useCase: 'Cas d’usage',
    expectedVolume: 'Volume attendu',
    message: 'Message',
    viewFullDetails: 'Voir tous les détails',
    pageOf: 'Page {page} sur {total}',
    justNow: 'À l’instant',
    minutesAgo: 'il y a {count} min',
    hoursAgo: 'il y a {count} h',
    daysAgo: 'il y a {count} j',
    statusUpdated: 'Statut mis à jour avec succès',
    statusUpdateFailed: 'Échec de la mise à jour du statut',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Chargement des détails utilisateur...',
    backToUsers: 'Retour aux utilisateurs',
    userNotFound: 'Utilisateur introuvable',
    superAdmin: 'Super Admin',
    allUsers: 'Tous les utilisateurs',
    editUser: 'Modifier utilisateur',
    accountActions: 'Actions du compte',
    accountActionsBody:
      'Les comptes suspendus ne peuvent pas se connecter ni créer de nouvelles sessions de vérification.',
    suspending: 'Suspension...',
    suspendAccount: 'Suspendre le compte',
    reactivating: 'Réactivation...',
    reactivateAccount: 'Réactiver le compte',
    updateUser: 'Mettre à jour l’utilisateur',
    planTier: 'Niveau d’offre',
    status: 'Statut',
    free: 'Gratuit',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Suspendu',
    saveChanges: 'Enregistrer',
    saving: 'Enregistrement...',
    accountInformation: 'Informations du compte',
    email: 'E-mail',
    name: 'Nom',
    company: 'Entreprise',
    emailVerified: 'E-mail vérifié',
    verified: 'Vérifié',
    notVerified: 'Non vérifié',
    authProvider: 'Fournisseur d’auth',
    created: 'Créé',
    lastUpdated: 'Dernière mise à jour',
    planDetails: 'Détails de l’offre',
    currentPlan: 'Offre actuelle',
    perMonth: 'mois',
    testVerifications: 'Vérifications de test',
    unlimited: 'Illimité',
    liveVerifications: 'Vérifications live',
    maxTestKeys: 'Max clés test',
    maxLiveKeys: 'Max clés live',
    suspendTitle: 'Suspendre ce compte ?',
    suspendDescription:
      'Les utilisateurs suspendus ne peuvent pas se connecter ni créer de nouvelles sessions de vérification. Les données existantes sont conservées.',
    suspend: 'Suspendre',
    reactivateTitle: 'Réactiver ce compte ?',
    reactivateDescription: 'La réactivation rétablit immédiatement l’accès.',
    reactivate: 'Réactiver',
    totalSessions: 'Sessions totales',
    completed: 'Terminées',
    failed: 'Échouées',
    thisMonth: 'Ce mois-ci',
    apiKeys: 'Clés API',
    totalKeys: 'Clés totales',
    activeKeys: 'Clés actives',
    publicId: 'ID public',
    environment: 'Environnement',
    lastUsed: 'Dernière utilisation',
    live: 'Live',
    test: 'Test',
    active: 'Active',
    revoked: 'Révoquée',
    never: 'Jamais',
    showingRange: '{start} à {end} sur {total} clés',
    previous: 'Précédent',
    next: 'Suivant',
    pageOf: 'Page {page} sur {total}',
  },
};

const fr: AdminUiCopy = {
  ...en,
  publicHeader: {
    features: 'Fonctionnalités',
    howItWorks: 'Fonctionnement',
    pricing: 'Tarifs',
    blog: 'Blog',
    docs: 'Documentation',
    signIn: 'Connexion',
    getApiKey: 'Obtenir une clé API',
  },
  login: {
    ...en.login,
    authFailed: 'Échec de l’authentification. Réessayez ou utilisez un autre fournisseur.',
    sessionFailed: 'Connexion fournisseur réussie, mais la session n’a pas pu être créée.',
    notConfigured: 'Le SSO n’est pas configuré pour cet environnement.',
    invalidState: 'Le contrôle de sécurité a échoué. Réessayez la connexion.',
    welcomeBack: 'Bon retour',
    subtitle: 'Connectez-vous à votre tableau de bord',
    accessRestricted: 'Accès restreint',
    accessDeniedAdminDisabled: 'Votre e-mail n’est pas autorisé. Contactez support@walletgate.app si vous pensez qu’il s’agit d’une erreur.',
    accessDeniedDefault: 'Votre e-mail n’est pas autorisé.',
    contactSupport: 'Contacter le support',
    backToHome: 'Retour à l’accueil',
    continueWithEmail: 'ou continuer avec l’e-mail',
    password: 'Mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    signIn: 'Se connecter',
    noAccount: 'Pas encore de compte ?',
    createAccount: 'Créer un compte',
  },
  register: {
    ...en.register,
    subtitle: 'Commencez avec un essai gratuit - sans carte bancaire',
    accessRestrictedBody: 'L’inscription est actuellement restreinte. Contactez support@walletgate.app pour obtenir l’accès.',
    welcome: 'Bienvenue sur WalletGate !',
    accountCreated: 'Votre compte a été créé',
    testApiKey: 'Votre clé API de test (à enregistrer maintenant) :',
    keyShownOnce: 'Cette clé ne sera affichée qu’une seule fois. Vous pouvez générer d’autres clés dans votre tableau de bord.',
    redirecting: 'Redirection vers le tableau de bord dans 5 secondes...',
    goNow: 'Aller au tableau de bord',
    fullName: 'Nom complet',
    companyOptional: 'Entreprise (optionnel)',
    password: 'Mot de passe',
    passwordStrength: 'Solidité du mot de passe',
    tooShort: 'Trop court (min. 8)',
    moderate: 'Moyen',
    strong: 'Fort',
    creating: 'Création du compte…',
    haveAccount: 'Vous avez déjà un compte ?',
    signIn: 'Se connecter',
    registrationFailed: 'Échec de l’inscription',
    teamInviteAcceptedTitle: 'Invitation d’équipe acceptée',
    teamInviteAcceptedBody: 'Vous avez été ajouté à l’équipe. Redirection vers le tableau de bord équipe...',
    inviteLockedEmailHint: 'Cet e-mail est lié à votre invitation d’équipe et ne peut pas être modifié.',
  },
  forgotPassword: {
    ...en.forgotPassword,
    checkEmail: 'Vérifiez votre e-mail',
    accountExistsNoticePrefix: 'Si un compte existe pour',
    accountExistsNoticeSuffix: 'vous recevrez sous peu un lien de réinitialisation du mot de passe.',
    nextSteps: 'Étapes suivantes :',
    stepInbox: 'Vérifiez votre boîte de réception',
    stepLink: 'Cliquez sur le lien de l’e-mail (valide 1 heure)',
    stepNewPassword: 'Créez un nouveau mot de passe',
    backToSignIn: 'Retour à la connexion',
    title: 'Mot de passe oublié ?',
    subtitle: 'Entrez votre e-mail et nous vous enverrons un lien de réinitialisation',
    sending: 'Envoi…',
    sendResetLink: 'Envoyer le lien',
    rememberPassword: 'Vous vous souvenez de votre mot de passe ?',
    signIn: 'Se connecter',
    sendFailed: 'Impossible d’envoyer l’e-mail de réinitialisation',
  },
  resetPassword: {
    ...en.resetPassword,
    invalidLink: 'Lien de réinitialisation invalide. Veuillez demander un nouveau lien.',
    tooShort: 'Le mot de passe doit contenir au moins 8 caractères',
    mismatch: 'Les mots de passe ne correspondent pas',
    failed: 'Échec de la réinitialisation du mot de passe',
    successTitle: 'Mot de passe réinitialisé',
    successBody: 'Votre mot de passe a été réinitialisé avec succès.',
    successCta: 'Se connecter maintenant',
    redirecting: 'Redirection vers la connexion dans 3 secondes...',
    title: 'Réinitialiser le mot de passe',
    subtitle: 'Entrez votre nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    passwordStrength: 'Solidité du mot de passe',
    resetting: 'Réinitialisation…',
    reset: 'Réinitialiser le mot de passe',
    rememberPassword: 'Vous vous souvenez de votre mot de passe ?',
    signIn: 'Se connecter',
  },
  notFound: {
    pageNotFound: 'Page introuvable',
    description: 'La page que vous recherchez n’existe pas ou a été déplacée.',
    goBack: 'Retour',
    goDashboard: 'Aller au tableau de bord',
    supportPrefix: 'Si vous pensez qu’il s’agit d’une erreur, contactez',
  },
  teamManagement: {
    title: 'Équipe',
    subtitleWithCount: '{current} sur {max} membres',
    subtitleDefault: 'Gérez les membres de votre équipe',
    leaveTeam: 'Quitter l’équipe',
    planLockedTitle: 'La gestion d’équipe est disponible sur les offres payantes',
    planLockedBody: 'Passez à Pro pour inviter jusqu’à 3 membres, ou Business pour jusqu’à 10.',
    viewPlans: 'Voir les offres',
    inviteMemberTitle: 'Inviter un membre',
    inviteCopiedTitle: 'Lien d’invitation copié',
    copyAgain: 'Recopier',
    inviteLinkHint: 'Partagez ce lien avec votre membre d’équipe. Expire dans 7 jours.',
    invitePlaceholder: 'collegue@entreprise.fr',
    roleAdmin: 'Admin',
    roleDeveloper: 'Développeur',
    roleViewer: 'Lecteur',
    roleAdminDescription: 'Accès complet',
    roleDeveloperDescription: 'Gérer les clés et le playground',
    roleViewerDescription: 'Accès en lecture seule',
    sendInvite: 'Envoyer l’invitation',
    sendingInvite: 'Envoi...',
    limitReachedPrefix: 'Limite de membres atteinte.',
    upgradePlan: 'Mettre à niveau',
    membersTitle: 'Membres',
    owner: 'Propriétaire',
    ownerDescription: 'Propriétaire du compte',
    joinedOn: 'Rejoint le {date}',
    pending: 'En attente',
    remove: 'Retirer',
    noMembers: 'Aucun membre pour le moment.',
    noMembersCta: 'Envoyez une invitation pour commencer.',
    pendingInvitesTitle: 'Invitations en attente',
    expires: 'Expire le {date}',
    copyLink: 'Copier le lien',
    revoke: 'Révoquer',
    leaveDialogTitle: 'Quitter l’équipe',
    leaveDialogBody:
      'Voulez-vous vraiment quitter cette équipe ? Vous perdrez l’accès aux ressources de ce compte et devrez être réinvité pour revenir.',
    leaveDialogConfirm: 'Quitter l’équipe',
    removeDialogTitle: 'Retirer un membre',
    removeDialogBody: 'Retirer {email} de l’équipe ? L’accès sera coupé immédiatement.',
    removeDialogConfirm: 'Retirer',
    revokeDialogTitle: 'Révoquer l’invitation ?',
    revokeDialogBody: 'L’invitation envoyée à {email} sera annulée. Cette personne ne pourra pas rejoindre l’équipe.',
    toastInviteCopied: 'Lien d’invitation copié',
    toastCopiedAgain: 'Copié à nouveau',
    toastInviteRevoked: 'Invitation révoquée',
    toastInviteRevokeFailed: 'Échec de la révocation',
    toastMemberRemoved: 'Membre retiré',
    toastMemberRemoveFailed: 'Impossible de retirer le membre',
    toastRoleUpdated: 'Rôle mis à jour',
    toastRoleUpdateFailed: 'Impossible de mettre à jour le rôle',
    toastLeftTeam: 'Vous avez quitté l’équipe',
    toastLeaveTeamFailed: 'Impossible de quitter l’équipe',
    inviteSendFailed: 'Impossible d’envoyer l’invitation',
  },
  auditLog: {
    title: 'Journal d’audit',
    subtitle: 'Suivez toutes les activités de votre compte.',
    searchPlaceholder: 'Rechercher par action, acteur, cible, IP, ID de requête ou ID d’événement',
    allCategories: 'Toutes les catégories',
    categorySecurity: 'Sécurité',
    categoryApi: 'API',
    categoryDataAccess: 'Accès aux données',
    categoryConfiguration: 'Configuration',
    allSeverities: 'Toutes les sévérités',
    severityInfo: 'Info',
    severityWarning: 'Avertissement',
    severityError: 'Erreur',
    severityCritical: 'Critique',
    clearFilters: 'Réinitialiser les filtres',
    loading: 'Chargement des journaux d’audit...',
    empty: 'Aucune entrée d’audit trouvée.',
    noMatches: 'Aucune entrée correspondante sur cette page.',
    timestamp: 'Horodatage',
    eventKey: 'Clé d’événement',
    action: 'Action',
    actor: 'Acteur',
    target: 'Cible',
    category: 'Catégorie',
    severity: 'Sévérité',
    source: 'Source',
    ipAddress: 'Adresse IP',
    eventId: 'ID d’événement',
    requestId: 'ID de requête',
    userAgent: 'Agent utilisateur',
    unknown: 'Inconnu',
    system: 'Système',
    pageSummary: 'Page {page} sur {totalPages} ({total} entrées)',
    previous: 'Précédent',
    next: 'Suivant',
    tapToExpand: 'Touchez pour voir les détails',
    details: 'Détails',
  },
  inviteAccept: {
    loading: 'Chargement des détails de l’invitation...',
    noToken: 'Aucun token d’invitation fourni.',
    invalidInviteDefault: 'Cette invitation est invalide, expirée ou déjà utilisée.',
    acceptFailedDefault: 'Impossible d’accepter l’invitation. Elle a peut-être expiré ou été révoquée.',
    inviteTitle: 'Vous avez été invité',
    inviteSubtitleNeedsLogin: 'Connectez-vous ou créez un compte pour rejoindre l’équipe',
    organization: 'Organisation',
    invitedBy: 'Invité par',
    yourRole: 'Votre rôle',
    role: 'Rôle',
    acceptInvite: 'Accepter l’invitation',
    joiningTeam: 'Connexion à l’équipe...',
    successTitle: 'Bienvenue !',
    successBodyWithOrg: 'Vous avez rejoint {organization}. Redirection...',
    successBodyDefault: 'Vous avez rejoint l’équipe. Redirection...',
    errorTitle: 'Impossible d’accepter l’invitation',
    goLogin: 'Aller à la connexion',
    signInToAccept: 'Se connecter pour accepter',
    createAccount: 'Créer un compte',
    signIn: 'Se connecter',
    needsRegisterTitle: 'Créez votre compte',
    needsRegisterBodyWithOrg: 'Créez un compte avec {email} pour rejoindre {organization}.',
    needsRegisterBodyDefault: 'Créez un compte avec {email} pour rejoindre l’équipe.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Développeur',
    roleViewer: 'Lecteur',
    roleAdminDescription: 'Accès complet à la gestion des clés, webhooks, facturation et équipe',
    roleDeveloperDescription: 'Créer et gérer les clés API, utiliser le playground',
    roleViewerDescription: 'Accès en lecture seule à la facturation et aux journaux d’audit',
  },
  keys: {
    ...en.keys,
    title: 'Clés API',
    createEnvLabel: 'Environnement de création de clé',
    testKey: 'Clé de test',
    liveKey: 'Clé live',
    createKey: '+ Créer une clé',
    maxKeysReached: 'Nombre maximal de clés {env} atteint pour votre offre',
    defaultHint: 'Utilisez Test par défaut pour un développement sûr.',
    limitReached: 'Limite de clés {env} atteinte.',
    revokeExisting: 'Passez à une offre supérieure ou révoquez une clé existante.',
    freePlan: 'Offre gratuite',
    freePlanHint: 'Passez à Pro (29 €/mois) pour 1 000 vérifications live, les webhooks et plus de clés API.',
    hideFilters: 'Masquer les filtres',
    showFilters: 'Afficher les filtres',
    allStatus: 'Tous les statuts',
    activeOnly: 'Actives uniquement',
    revokedOnly: 'Révoquées uniquement',
    allEnvironments: 'Tous les environnements',
    keyNamePlaceholder: 'ex. API Production',
    loading: 'Chargement des clés API...',
    emptyAll: 'Aucune clé API pour le moment.',
    emptyByStatus: 'Aucune clé API {status} trouvée.',
    unnamed: 'Sans nom',
    revoked: 'Révoquée',
    active: 'Active',
    unknown: 'Inconnu',
    rotate: 'Faire tourner',
    revoke: 'Révoquer',
    rotateConfirmTitle: 'Faire tourner la clé API ?',
    rotateConfirmDescription: 'Une nouvelle clé sera générée pour « {name} » et la clé actuelle sera immédiatement révoquée.',
    revokeConfirmTitle: 'Révoquer la clé API ?',
    revokeConfirmDescription: '« {name} » sera désactivée définitivement. Cette action est irréversible.',
  },
  webhooks: {
    ...en.webhooks,
    upgradeRequired: 'Mise à niveau requise',
    upgradeBody: 'Les webhooks sont disponibles dans l’offre Business.',
    subtitle: 'Recevez les événements en temps réel pour vos intégrations',
    createTitle: 'Créer un webhook',
    createWebhook: 'Créer le webhook',
    signingSecret: 'Secret de signature webhook (à enregistrer)',
    existingTitle: 'Webhooks existants',
    empty: 'Aucun webhook pour le moment.',
    enabled: 'Activé',
    disabled: 'Désactivé',
    inspectorTitle: 'Inspecteur de webhook',
    inspectorLocked: 'L’inspecteur de webhook est disponible sur les offres Pro et Business.',
    loadingDeliveries: 'Chargement des livraisons...',
    emptyDeliveries: 'Aucune livraison webhook pour le moment.',
    retrying: 'Nouvelle tentative…',
    retryDelivery: 'Relancer la livraison',
    selectDelivery: 'Sélectionnez une livraison à inspecter.',
  },
  billing: {
    ...en.billing,
    breadcrumb: 'Facturation',
    staffBypass: 'Compte staff : les droits sont contournés. La facturation et les quotas ne s’appliquent pas.',
    paidComingSoonTitle: 'Offres payantes bientôt disponibles.',
    paidComingSoonBody: 'Nous finalisons notre intégration de paiement. En attendant, vous pouvez utiliser l’offre gratuite.',
    upgradeDetected: 'Mise à niveau vers {plan} détectée. Vous avez maintenant accès aux fonctionnalités premium.',
    upgradePending: 'Si vous avez terminé le paiement pour {plan}, la mise à niveau sera détectée automatiquement sous peu.',
    refreshNow: 'Actualiser',
    goSecurity: 'Aller à Sécurité',
    title: 'Offres & Facturation',
    subtitle: 'Choisissez l’offre adaptée à vos besoins. Mise à niveau à tout moment.',
    manageBilling: 'Gérer la facturation',
    currentPlan: 'Offre actuelle',
    tierLabel: 'Niveau : {tier}',
    noBillingStaff: 'Pas de facturation pour le staff',
    perMonth: 'par mois',
    billingCycle: 'Cycle de facturation',
    nextBillDate: 'Prochaine date de facturation',
    noBillingCycle: 'Aucun cycle de facturation pour le moment. Passez à une offre supérieure.',
    noBillingCycleStaff: 'Compte interne. Aucun cycle de facturation.',
    manageBillingArrow: 'Gérer la facturation →',
    freeFeatures: [
      'Vérifications de test illimitées',
      '100 vérifications live/mois',
      '5 clés de test, 2 clés live',
      'Sandbox de base (réussite/échec)',
      'Exemples de code d’intégration (10 langages)',
      'Support e-mail',
    ],
    proFeatures: [
      'Vérifications de test illimitées',
      '1 000 vérifications live/mois',
      '10 clés de test, 5 clés live',
      "Jusqu'à 3 membres d'équipe",
      '20+ scénarios de test préconfigurés',
      'Inspecteur de webhook',
      'SLA 99,9 %',
      'Support e-mail',
    ],
    businessFeatures: [
      'Vérifications de test illimitées',
      '10 000 vérifications live/mois',
      '10 clés de test, 10 clés live',
      "Jusqu'à 10 membres d'équipe",
      'Tout ce qui est inclus dans Pro',
      'Création de scénarios personnalisés',
      'Rapport de préparation EUDI (export conformité)',
      'Simulation pays (DE, FR, ES, IT, NL)',
      'Analyses avancées',
      'Support e-mail prioritaire',
    ],
    mostPopular: 'Le plus populaire',
    activePlan: 'Offre active',
    upgradeToPro: 'Passer à Pro',
    upgradeToBusiness: 'Passer à Business',
    contactSales: 'Contacter les ventes',
  },
  dashboard: {
    ...en.dashboard,
    overview: 'Vue d\'ensemble',
    noPriorData: 'Aucune donnée antérieure',
    noChange: 'Aucun changement',
    vsLastPeriod: '{value}% par rapport à la période précédente',
    last24Hours: 'Dernières 24 heures',
    last30Days: '30 derniers jours',
    last7Days: '7 derniers jours',
    hour: 'heure',
    day: 'jour',
    totalVerifications: 'Vérifications totales',
    successRate: 'Taux de réussite',
    activeSessions: 'Sessions actives',
    currentlyInProgress: 'En cours actuellement',
    verificationTrend: 'Tendance de vérification',
    trackVerificationVolume: 'Suivez le volume de vérification au fil du temps',
    total: 'Total',
    completed: 'Terminées',
    failed: 'Échouées',
    average: 'Moyenne',
    peak: 'Pic',
    perBucket: 'Par {bucket}',
    avgPerBucket: 'Moy. par {bucket}',
    noDataYet: 'Aucune donnée pour le moment',
    verificationsWillAppear: 'Les vérifications apparaîtront ici une fois commencées',
    currentPlan: 'Offre actuelle',
    manage: 'Gérer',
    staff: 'Staff',
    internalAccount: 'Compte interne',
    noBillingForStaff: 'Pas de facturation pour le staff',
    liveVerificationsThisMonth: 'Vérifications live (ce mois)',
    upgradeForLiveAccess: "Passez à une offre supérieure pour l'accès live",
    nextBillDate: 'Prochaine date de facturation',
    status: 'Statut',
    daysRemaining: '{days} jours restants',
    renewingSoon: 'Renouvellement proche',
    gracePeriod: 'Période de grâce',
    daysToUpdatePayment: '{days} jours pour mettre à jour le paiement',
    active: 'Actif',
    allSystemsOperational: 'Tous les systèmes opérationnels',
    advancedAnalytics: 'Analyses avancées',
    detailedBreakdown: 'Détail par environnement et clé API',
    exportCsv: 'Exporter CSV',
    byEnvironment: 'Par environnement',
    byStatus: 'Par statut',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top clés API (ce mois)',
    advancedAnalyticsUpgrade: "Analyses avancées. Passez à une offre supérieure pour des détails et l'export CSV.",
    upgrade: 'Mettre à niveau',
    developerResources: 'Ressources développeur',
    documentation: 'Documentation',
    typescriptSdk: 'SDK TypeScript',
    onboardingWelcome: 'Bienvenue sur WalletGate',
    youreAllSet: 'Tout est prêt !',
    accountReady: 'Votre compte est prêt. Effectuez votre première vérification en moins de 5 minutes.',
    getStartedIn3Steps: 'Démarrez en 3 étapes',
    getYourApiKey: 'Obtenez votre clé API',
    apiKeyReady: "Vous avez déjà une clé API prête à l'emploi",
    createFirstApiKey: 'Créez votre première clé API pour authentifier vos requêtes',
    viewApiKeys: 'Voir les clés API',
    createApiKey: 'Créer une clé API',
    installSdk: 'Installer le SDK',
    addWalletGate: 'Ajoutez WalletGate à votre projet en une seule commande',
    copyCommand: 'Copier la commande',
    copied: 'Copié !',
    makeFirstRequest: 'Faites votre première requête',
    startVerifying: 'Commencez à vérifier les identités numériques en quelques lignes de code',
    viewDocumentation: 'Voir la documentation',
    quickStartExample: 'Exemple de démarrage rapide',
    copyCode: 'Copier le code',
    apiDocumentation: 'Documentation API',
    apiDocumentationDesc: 'Référence API complète et guides',
    codeExamples: 'Exemples de code',
    codeExamplesDesc: 'Intégrations types dans plusieurs langages',
    support: 'Support',
    supportDesc: "Obtenez l'aide de notre équipe",
    community: 'Communauté',
    communityDesc: "Rejoignez d'autres développeurs",
    complete: 'Terminé',
    noPriorRange: 'Aucune donnée pour les {range} précédents',
    noChangeVsPrior: 'Aucun changement par rapport aux {range} précédents',
    vsPriorRange: '{value}% par rapport aux {range} précédents',
  },
  playground: {
    ...en.playground,
    export: 'Exporter',
    onboardingConfigureTitle: 'Configurer les contrôles',
    onboardingConfigureMessage: 'Choisissez un modèle rapide ou ajoutez des contrôles, puis lancez la vérification.',
    onboardingFlowTitle: 'Suivre le flux en direct',
    onboardingFlowMessage: 'Suivez chaque étape de la session, du handoff à la décision.',
    onboardingShipTitle: 'Livrer plus vite',
    onboardingShipMessage: 'Exportez cette exécution en JSON ou copiez le code d’intégration.',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Restez informé de l\'activité du compte, des alertes de sécurité et des mises à jour d\'équipe.',
    searchPlaceholder: 'Rechercher des notifications...',
    allCategories: 'Toutes les catégories',
    categorySecurity: 'Sécurité',
    categoryBilling: 'Facturation',
    categoryApi: 'API',
    categoryTeam: 'Équipe',
    allStatus: 'Toutes',
    unreadOnly: 'Non lues',
    readOnly: 'Lues',
    clearFilters: 'Réinitialiser les filtres',
    loading: 'Chargement des notifications...',
    empty: 'Aucune notification pour le moment. Les activités apparaîtront ici au fur et à mesure.',
    noMatches: 'Aucune notification ne correspond à vos filtres.',
    markRead: 'Marquer comme lu',
    markAllRead: 'Tout marquer comme lu',
    delete: 'Supprimer',
    viewAll: 'Voir toutes les notifications',
    preferences: 'Préférences',
    preferencesTitle: 'Préférences de notification',
    preferencesSubtitle: 'Choisissez comment être notifié pour chaque catégorie.',
    inApp: 'In-App',
    email: 'E-mail',
    savePreferences: 'Enregistrer les préférences',
    savedPreferences: 'Préférences de notification enregistrées.',
    failedSavePreferences: 'Impossible d\'enregistrer les préférences de notification.',
    category: 'Catégorie',
    severity: 'Sévérité',
    timestamp: 'Heure',
    pageSummary: 'Page {page} sur {total}',
    previous: 'Précédent',
    next: 'Suivant',
    bellNoNotifications: 'Aucune nouvelle notification',
    bellRecentTitle: 'Notifications',
    severityInfo: 'Info',
    severityWarning: 'Avertissement',
    severityError: 'Erreur',
    severityCritical: 'Critique',
    toastMarkedRead: 'Notification marquée comme lue.',
    toastMarkedAllRead: 'Toutes les notifications marquées comme lues.',
    toastDeleted: 'Notification supprimée.',
    toastDeleteFailed: 'Impossible de supprimer la notification.',
    toastNewSingle: 'Vous avez 1 nouvelle notification.',
    toastNewMultiple: 'Vous avez {count} nouvelles notifications.',
  },
  ...frExtendedSections,
};

export default fr;
